<template>
  <el-dialog
    :visible="true"
    :fullscreen="true"
    :close-on-click-modal="false"
    :before-close="handleClose"
  >
    <div slot="title">
      {{ title }}
      <el-tag
        v-if="stockMovement.status && (type === 'update' || type === 'view')"
        size="small"
        :type="getStatus().tag"
        style="margin-left: 5px;"
      >
        {{ getStatus().label }}
      </el-tag>
    </div>
    <div v-loading="loading">
      <el-form
        ref="stockMovementForm"
        label-position="top"
        :model="stockMovement"
        :rules="rules"
      >
        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item
              prop="source_storage_id"
            >
              <div
                slot="label"
                style="display: inline-block;"
              >
                Átadó raktár
                <template v-if="$config('SHOW_STORAGE_TOOLTIPS')">
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :content="`Ebből a raktárból helyezzük át az árut. ${$config('STORAGE_TOOLTIP_SUFFIX')}`"
                    placement="right"
                  >
                    <i class="el-icon-info" />
                  </el-tooltip>
                </template>
              </div>
              <el-select
                v-model="stockMovement.source_storage_id"
                style="width: 100%;"
                value-key="id"
                :disabled="type === 'view'"
                @change="onSourceStorageChange"
              >
                <el-option
                  v-for="storage in storages"
                  :key="`source_storage_${storage.id}`"
                  :value="storage.id"
                  :label="storage.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item
              prop="target_storage_id"
            >
              <div
                slot="label"
                style="display: inline-block;"
              >
                Átvevő raktár
                <template v-if="$config('SHOW_STORAGE_TOOLTIPS')">
                  <el-tooltip
                    class="item"
                    effect="dark"
                    content="Ebbe a raktárba helyezzük át az árut."
                    placement="right"
                  >
                    <i class="el-icon-info" />
                  </el-tooltip>
                </template>
              </div>
              <el-select
                v-model="stockMovement.target_storage_id"
                style="width: 100%;"
                value-key="id"
                :disabled="type === 'view'"
              >
                <el-option
                  v-for="storage in storages"
                  :key="`target_storage_${storage.id}`"
                  :value="storage.id"
                  :label="storage.name"
                  :disabled="storage.id === stockMovement.source_storage_id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <product-items
          ref="productItems"
          :default-items="stockMovement.items"
          :enabled-elements="enabledElements"
          :enabled-columns="type === 'view' ? ['product', 'productPackType', 'expirationDateSelector', 'count', 'supplierSelector'] : ['product', 'productPackType', 'expirationDateSelector', 'count', 'actions', 'supplierSelector']"
          compare-type="completion"
          :storage-id="stockMovement.source_storage_id"
        />
      </el-form>
    </div>
    <div
      v-if="type !== 'view'"
      slot="footer"
      class="dialog-footer"
    >
      <el-button
        :loading="saveButtonLoading"
        type="primary"
        @click="store"
      >
        {{ buttonText }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import ProductItems from '../../utils/ProductItems/index';

export default {
  components: {
    ProductItems,
  },
  props: ['type', 'stockMovementId'],
  data() {
    return {
      loading: false,
      buttonLoading: false,
      saveButtonLoading: false,
      stockMovement: {
        items: [],
        source_storage_id: null,
        target_storage_id: null,
      },
      rules: {
        source_storage_id: [
          {
            required: true,
            message: 'Az átadó raktár kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        target_storage_id: [
          {
            required: true,
            message: 'Az átvevő raktár kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        target_storage_id: [
          {
            required: true,
            message: 'Az átvevő raktár kiválasztása kötelező',
            trigger: 'change'
          }
        ]
      },
    };
  },
  computed: {
    title() {
      if (this.type === 'add') {
        return 'Új készletmozgás';
      } else if (this.type === 'view') {
        return 'Készletmozgás megtekintése';
      } else if (this.type === 'update') {
        return 'Készletmozgás módosítása';
      } else {
        return '';
      }
    },
    buttonText() {
      if (this.type === 'add') {
        return 'Hozzáadás';
      } if (this.type === 'update') {
        return 'Módosítás';
      } else {
        return '';
      }
    },
    enabledElements() {
      if (this.type === 'view') {
        return [];
      } else if (this.type === 'add' || this.type === 'update') {
        return ['addButton', 'deleteButton', 'product', 'pack', 'count', 'expirationDateSelector', 'supplierSelector']
      }

      return []
    },
    storages() {
      return this.$store.getters['storage/ownStorages'];
    },
  },
  async mounted() {
    this.loading = true;
    const requiredFetches = [];
    if (!this.storages.length) {
      requiredFetches.push(this.$store.dispatch('storage/fetchAll', ''));
    }

    await Promise.all(requiredFetches);

    if (this.stockMovementId) {
      this.stockMovement = await this.$store.dispatch('stockMovement/fetch', this.stockMovementId);
      if(this.type === 'add') {
        this.stockMovement.items = this.stockMovement.items.map(item => {
          delete item.id;
          return item;
        });
      }
    } else {
      this.stockMovement.source_storage_id = this.storages[0].id;
    }
    this.loading = false;
  },
  methods: {
    getStatus() {
      if (this.stockMovement.status === 'recorded') {
        return {
          label: 'Rögzítve',
          tag: 'primary'
        }
      } else if (this.stockMovement.status === 'in-progress') {
        return {
          label: 'Folyamatban',
          tag: 'info'
        }
      } else if (this.stockMovement.status === 'done') {
        return {
          label: 'Teljesítve',
          tag: 'success'
        }
      }
    },
    onSourceStorageChange() {
      this.stockMovement.items = [];
      this.stockMovement.target_storage_id = null
    },
    handleClose() {
      if (this.type === 'view') {
        return this.$router.replace('/processes/stock-movements');
      }
      this.$confirm('Biztosan elveted a módosításokat?', {
        confirmButtonText: 'Igen',
        type: 'warning',
      })
        .then(() => {
          this.$router.replace('/processes/stock-movements');
        })
        .catch(() => {
        });
    },

    async store() {

      this.$refs.stockMovementForm.validate(async valid => {
        if (valid) {
          this.stockMovement.items = this.$refs['productItems'].getItems();
          if (!this.stockMovement.items.length) {
            return this.$messageWrapper.warning('Minimum 1 termék hozzáadása kötelező.');
          } else if (!this.$refs['productItems'].validateProducts()) {
            return this.$messageWrapper.warning('Termékadatok hibásan vannak kitöltve.');
          } else if (!this.$refs['productItems'].validateSuppliers()) {
            return this.$messageWrapper.warning('Minden terméknél kötelező megadni a beszállítót');
          }else if (!this.$refs['productItems'].validateExpirationDates()) {
            return this.$messageWrapper.warning('Minden terméknél kötelező megadni a szavatossági időt');
          }

          this.saveButtonLoading = true;
          try {
            if (this.type === 'add') {
              await this.$store.dispatch('stockMovement/create', this.stockMovement);
            } else if (this.type === 'update') {
              await this.$store.dispatch('stockMovement/update', this.stockMovement);
            }
            this.$store.dispatch('stockMovement/fetchAllWithPagination');
            this.saveButtonLoading = false;
            this.$router.replace('/processes/stock-movements');
          } catch (e) {
            console.error(e)
          }
          this.saveButtonLoading = false;
        }
      });
    },
  },
};
</script>

<style lang="scss">
  .price-sum {
    margin-top: 20px;
    text-align: right;
    font-size: 18px;
    > div {
      display: inline-block;
    }
  }
</style>
